const ApplicationContext = require("../config/ApplicationContext.js");
const Utilities = require("./Utilities.js");
const RegionRegistry = require("../services/RegionRegistry.js");

const REGION_COOKIE = "lastHubRegion";
// US content-servers report their region as "usa" (FUSION_REGION_ID=USA), but
// the client uses "us" everywhere (home region, region-map keys). Treat them as
// the same region so e.g. an invite-accept redirect carrying ?region=usa still
// resolves to the US server.
const REGION_ALIASES = { usa: "us" };
var regionsPromise = null;
var regionsMap = {};

/**
 * ASSET IDs (libraries, packages, 3d models) in non-US regions are base64-encoded
 * as "<REGION>_<id>" - e.g. "SU5EXzExMjU2MTA" decodes to "IND_1125610". Legacy / US
 * asset IDs are plain integers (e.g. "12345"). NOTE: hub / managed-folder IDs are
 * always plain integers and never carry a region - the region for a folder comes
 * from the region cookie, not from its id.
 *
 * Decoding the region out of an asset ID lets the app route a request to the region
 * the asset actually lives in - including for deep links pasted straight into the
 * browser - and keep the region cookie in sync for the non-asset pages that follow.
 */
class RegionUtil {

    constructor(){}

    //Source for the regions available in a given environment. Results are cached
    //and only loaded from the FRR the first time its called.
    getRegions(){
        // We already got the regions cached
        if(regionsPromise) return regionsPromise;

        // Check if R13N is enabled, if not, only allow the home region
        if (!RegionUtil.isR13Enabled()) return Promise.resolve(this._homeOnly())

        // Return a list of all the regions otherwise
        regionsPromise = this._resolveRegions()
            .then(map => { regionsMap = map || {}; return regionsMap; })
            .catch(err => {
                console.warn("RegionUtil: falling back to home region only", err);
                regionsMap = this._homeOnly();
                return regionsMap;
            });
        return regionsPromise;
    }

    _resolveRegions(){
        // An explicit region map (local dev / offline) short-circuits the CS round-trip.
        const configured = ApplicationContext.configuredRegions;
        if(configured && Object.keys(configured).length > 0){
            return Promise.resolve(configured);
        }

        // Otherwise this region's content-server owns the FRR fetch (+ test-FRR flag)
        // and returns the parsed region map on its authenticated user-info surface.
        return RegionRegistry.loadFromContentServer();
    }

    _homeOnly(){
        const home = ApplicationContext.getHomeRegion();
        const server = ApplicationContext.contentServer && ApplicationContext.contentServer.server;
        const map = {};
        if(home && server) map[home] = server;
        return map;
    }

    // Map region-id aliases to the canonical id the client uses (e.g. usa -> us).
    normalizeRegionId(regionId){
        if(!regionId) return regionId;
        const id = String(regionId).toLowerCase();
        return REGION_ALIASES[id] || id;
    }

    // Returns the region specific domain name for the CS
    getRegionServer(regionId){
        const rid = this.normalizeRegionId(regionId) || ApplicationContext.getCurrentRegion();
        return (regionsMap && regionsMap[rid]) || (ApplicationContext.contentServer && ApplicationContext.contentServer.server);
    }

    regionFromServerUrl(url){
        try {
            const origin = new URL(url).origin;
            for (const [region, server] of Object.entries(regionsMap || {})) {
                if (!server) continue;

                try {
                    if (new URL(server).origin === origin) return region
                } catch(e) {}
            }
        } catch(e) {}

        return ApplicationContext.getHomeRegion();
    }

    /**
     * ASSET IDs (libraries, packages, 3d models) in non-US regions are base64-encoded
     * as "<REGION>_<id>" - e.g. "SU5EXzExMjU2MTA" decodes to "IND_1125610". Legacy / US
     * asset IDs are plain integers (e.g. "12345"). NOTE: hub / managed-folder IDs are
     * always plain integers and never carry a region - the region for a folder comes
     * from the region cookie, not from its id.
     */
    regionFromId(id){
        const home = ApplicationContext.getHomeRegion();
        if(id === undefined || id === null) return home;

        let s = String(id);
        // Legacy / US ids are plain integers - no region encoded.
        if(/^\d+$/.test(s)) return home;

        try {
            // Tolerate url-safe base64 and missing padding.
            let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
            while(b64.length % 4) b64 += "=";

            let decoded = (typeof atob === "function")
                ? atob(b64)
                : Buffer.from(b64, "base64").toString("utf8");

            let match = decoded.match(/^([A-Za-z]+)_\d+$/);
            if(match) return match[1].toLowerCase();
        } catch(e) {
            // not a regional id - fall through to home
        }
        return home;
    }

    syncRegionFromId(id){
        return this.syncRegion(this.regionFromId(id));
    }

    // Persist + activate a region we already know directly (e.g. the region id
    // carried on an invite-accept redirect) rather than decoded from an asset
    // id. Keeps the lastHubRegion cookie in sync so the non-asset pages that
    // follow route to the same region.
    syncRegion(regionId){
        const region = this.normalizeRegionId(regionId) || ApplicationContext.getHomeRegion();
        if(region !== Utilities.getCookie(REGION_COOKIE)){
            Utilities.setCookie(`${REGION_COOKIE}=${region}`);
        }
        ApplicationContext.setCurrentRegion(region);
        return region;
    }

    static isR13Enabled() {
        return ApplicationContext.isR13Enabled();
    }

}

if (typeof window !== "undefined") {
    window.RegionUtil = RegionUtil;
}

module.exports = new RegionUtil();
